/* BankingPreview.jsx — Path #5 full preview page (USER view).
 *
 * Same hero, sticky pill nav, Section/InfoCard renderers and cream palette as
 * before. Content package v2.0.0 (2026-09-08) adds the AI-agent workforce
 * section, the conditional automation-outlook section, knowledge-check
 * explanations (attempts saved under quiz_version 2.0.0) and the shared
 * guidance modules rendered once via the existing Accordion pattern.
 *
 *   • Fetches single role from /api/banking/{catalog_number} (gate-protected).
 *   • Shared modules from /api/banking/shared-guidance (same gate).
 *   • Reading position is restored per user + role from localStorage.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { recordCardEvent } from '../lib/clarityCard';
import { useAuth } from '../lib/auth';
import {
  ArrowLeft, Loader2, Quote, Briefcase, BookOpen, Map, Wrench, Lightbulb,
  MessageCircle, Brain, Bot, TrendingDown, ShieldAlert,
  GraduationCap, Activity, Sparkles, Layers, History,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  Section, InfoCard, Bullets, ChipList, PillNav, QuizYesNoMcq,
} from '../components/preview-ui/PreviewPrimitives';
import { AgentWorkforceSection, AutomationOutlookSection } from '../components/banking/BankingAgentSections';
import BankingSharedGuidance from '../components/banking/BankingSharedGuidance';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'why-you-matter', label: 'How to Prepare',        icon: Sparkles },
  { id: 'eligibility',   label: 'Entry Requirements',     icon: GraduationCap },
  { id: 'daily-work',    label: 'Daily Work',             icon: Briefcase },
  { id: 'transfers',     label: 'Transfers & Pressure',   icon: Map },
  { id: 'ai-agents',     label: 'AI-Agent Workforce',     icon: Bot },
  { id: 'automation',    label: 'Position Outlook',       icon: TrendingDown },
  { id: 'skills',        label: 'Learning Priorities',    icon: Wrench },
  { id: 'tools',         label: 'Tools',                  icon: BookOpen },
  { id: 'projects',      label: 'Practice',               icon: Lightbulb },
  { id: 'interview',     label: 'Interview Answer',       icon: MessageCircle },
  { id: 'readiness',     label: 'Quick Readiness',        icon: Activity },
  { id: 'quiz',          label: 'Check Understanding',    icon: Brain },
  { id: 'ai-ecosystem',  label: 'Shared AI Guidance',     icon: Layers },
];

const READ_KEY = (uid, cn) => `lxt_banking_read_v1:${uid || 'anon'}:${cn}`;

/* scoring_guide v2 strings look like: 'Legacy key: show "Strong knowledge check". Four or five…' */
function splitGuide(s) {
  const m = /^Legacy key: show [“"]([^”"]+)[”"]\.\s*(.*)$/s.exec(s || '');
  return m ? { label: m[1], text: m[2] } : { label: null, text: s };
}

export default function BankingPreview() {
  const { catalogNumber } = useParams();
  const { user } = useAuth();
  const [role, setRole] = useState(null);
  const [safetyNote, setSafetyNote] = useState('');
  const [shared, setShared] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [saveState, setSaveState] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const restoredRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(''); setRole(null); restoredRef.current = false;
    (async () => {
      try {
        const [rRes, sRes, gRes, aRes] = await Promise.all([
          axios.get(`${API}/api/banking/${catalogNumber}`),
          axios.get(`${API}/api/banking/safety-note`).catch(() => ({ data: {} })),
          axios.get(`${API}/api/banking/shared-guidance`).catch(() => ({ data: {} })),
          axios.get(`${API}/api/banking/quiz/${catalogNumber}/attempts`).catch(() => ({ data: {} })),
        ]);
        if (!cancelled) {
          setRole(rRes.data || null);
          if (rRes.data) recordCardEvent('preview_explored', { preview_id: `banking:${catalogNumber}`, name: rRes.data.role_name });
          setSafetyNote(sRes.data?.note || '');
          setShared(gRes.data?.shared || null);
          setAttempts(aRes.data?.attempts || []);
        }
      } catch (e) {
        if (cancelled) return;
        const st = e?.response?.status;
        if (st === 404) setErr('This preview is being added.');
        else if (st === 403) setErr('Open this preview from your picks.');
        else if (st === 401) setErr('Your session expired — please reload the page.');
        else if (st === 423) setErr(e?.response?.data?.detail?.message || 'That preview is not in your set.');
        else setErr(e?.response?.data?.detail || 'Could not load this preview.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [catalogNumber]);

  /* Saved reading position — restore once after content renders, then track scroll. */
  useEffect(() => {
    if (!role || restoredRef.current) return;
    restoredRef.current = true;
    let y = 0;
    try { y = Number(JSON.parse(localStorage.getItem(READ_KEY(user?.user_id, catalogNumber)) || '{}').y) || 0; } catch { y = 0; }
    requestAnimationFrame(() => window.scrollTo(0, y));
  }, [role, user, catalogNumber]);

  useEffect(() => {
    if (!role) return undefined;
    let t = null;
    const onScroll = () => {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        try { localStorage.setItem(READ_KEY(user?.user_id, catalogNumber), JSON.stringify({ y: window.scrollY, at: Date.now() })); } catch { /* ignore */ }
      }, 250);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (t) clearTimeout(t); };
  }, [role, user, catalogNumber]);

  const saveAttempt = useCallback(async ({ mcq, yn }) => {
    const mcqs = role?.role_quiz?.mcqs || [];
    const answers = {};
    mcqs.forEach((q, i) => { if (q.id && mcq[i] !== undefined) answers[q.id] = mcq[i]; });
    setSaveState('saving');
    try {
      const r = await axios.post(`${API}/api/banking/quiz/${catalogNumber}/attempts`, { answers, reflections: yn });
      setAttempts((prev) => [r.data, ...prev]);
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [role, catalogNumber]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[70vh]" data-testid="bank-preview-loading">
          <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (err || !role) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md" data-testid="bank-preview-error">
            <p className="text-2xl font-bold text-gray-900 mb-2">
              {err || 'This preview is being added.'}
            </p>
            <Button asChild className="mt-4 bg-emerald-600 hover:bg-emerald-700">
              <Link to="/banking">Back to all banking roles</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const H = shared?.heading_overrides || {};
  const labels = shared?.page?.role_section_labels || {};
  const elig = role.verified_eligibility_reality_check || {};
  const verif = role.verification || {};
  const quiz = role.role_quiz || null;
  const guide = quiz?.scoring_guide || {};
  const parsed = { high: splitGuide(guide.high_fit), medium: splitGuide(guide.medium_fit), low: splitGuide(guide.low_fit) };
  const learn = role.learn_next?.length ? role.learn_next : (role.skills_students_should_build || []);
  const practice = role.practice || null;

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 md:pt-36 pb-12 px-4 md:px-6 bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-600 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 -left-10 w-36 h-36 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-4xl mx-auto relative">
          <Link
            to="/banking"
            data-testid="bank-preview-back"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-5 transition-colors font-semibold"
          >
            <ArrowLeft size={14} /> Back to all banking roles
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-bold">
            {role.role_family && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full" data-testid="bank-preview-family">
                {role.role_family}
              </span>
            )}
            <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full">Banking · AI-Agent Workforce</span>
            {role.content_version && (
              <span className="bg-white/15 backdrop-blur px-3 py-1 rounded-full" data-testid="bank-preview-version">Content v{role.content_version}</span>
            )}
          </div>
          <h1
            data-testid="bank-preview-title"
            className="text-3xl md:text-5xl font-black leading-tight mb-6"
          >
            {role.role_name}
          </h1>
          {role.daily_work_preview && (
            <blockquote
              className="relative bg-white/10 backdrop-blur border border-white/20 rounded-3xl px-5 py-5 md:px-7 md:py-6"
              data-testid="bank-preview-blockquote"
            >
              <Quote size={20} className="text-amber-300 mb-2.5" />
              <p className="text-base md:text-lg text-white leading-relaxed italic">{role.daily_work_preview}</p>
            </blockquote>
          )}
        </div>
      </section>

      <PillNav sections={SECTIONS} testidPrefix="bank-preview-nav" />

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-8 space-y-6">

        {/* Standing important_safety_note banner */}
        {safetyNote && (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50 p-4 md:p-5"
            data-testid="bank-preview-safety"
          >
            <div className="flex items-start gap-3">
              <ShieldAlert size={20} className="text-amber-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] uppercase font-bold tracking-widest text-amber-800 mb-1">
                  Important safety note
                </p>
                <p className="text-sm text-gray-800 leading-relaxed">{safetyNote}</p>
              </div>
            </div>
          </div>
        )}

        {/* Shared page opening — once per role page */}
        {shared?.page?.opening_lines?.length > 0 && (
          <InfoCard label={shared.page.title || 'Banking careers in the AI-agent era'} tone="teal">
            <div data-testid="bank-preview-opening"><Bullets items={shared.page.opening_lines} tone="teal" /></div>
          </InfoCard>
        )}

        {/* why_you_matter_in_the_ai_era → "How to Prepare as the Work Changes" */}
        {role.why_you_matter_in_the_ai_era && (
          <Section id="why-you-matter" icon={Sparkles} title={H.why_you_matter_in_the_ai_era || 'How to Prepare as the Work Changes'} accent="teal">
            <p data-testid="bank-preview-why-you-matter">{role.why_you_matter_in_the_ai_era}</p>
          </Section>
        )}

        {/* Entry requirements — check the current notice */}
        {Object.keys(elig).length > 0 && (
          <Section id="eligibility" icon={GraduationCap} title={H.verified_eligibility_reality_check || 'Entry Requirements — Check the Current Notice'} accent="emerald">
            <div className="grid md:grid-cols-2 gap-3">
              {elig.age && <InfoCard label="Age" tone="mint">{elig.age}</InfoCard>}
              {elig.education_branch_rule && <InfoCard label="Education & branch rule" tone="sky">{elig.education_branch_rule}</InfoCard>}
              {elig.entry_route && <InfoCard label="Entry route" tone="violet">{elig.entry_route}</InfoCard>}
              {elig.student_warning && <InfoCard label="Warning" tone="rose">{elig.student_warning}</InfoCard>}
            </div>
            {(elig.route_type || elig.directory_note) && (
              <p className="mt-3 text-sm text-gray-700" data-testid="bank-preview-route-type">
                {elig.route_type && <span className="font-semibold">Route type: {String(elig.route_type).replace(/_/g, ' ')}. </span>}
                {elig.directory_note}
              </p>
            )}
            {Array.isArray(elig.sources_resolved) && elig.sources_resolved.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="bank-preview-elig-sources">
                {elig.sources_resolved.map((s) => (
                  <li key={s.id} className="text-sm break-all">
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 underline">{s.title}</a>
                    {s.use_limit && <span className="text-gray-500"> — {s.use_limit}</span>}
                  </li>
                ))}
              </ul>
            )}
            {/* verification — content review, never eligibility proof */}
            {verif.status && (
              <div className="mt-3 space-y-3" data-testid="bank-preview-verification">
                <InfoCard label={`${H.verification || 'Content Review and Sources'} · ${verif.status}${verif.reviewed_on ? ` · reviewed ${verif.reviewed_on}` : ''}`} tone="cream">
                  <p className="text-sm font-semibold text-amber-900 mb-1">
                    A content-review date is not proof of current recruitment eligibility{elig.eligibility_certified === false ? ' — eligibility not certified' : ''}.
                  </p>
                  {verif.note}
                  {Array.isArray(verif.sources) && verif.sources.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {verif.sources.map((s, i) => (
                        <li key={i} className="text-sm break-all">
                          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:text-emerald-900 underline">{s.title}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </InfoCard>
              </div>
            )}
            <p className="mt-4 text-[12px] uppercase font-bold tracking-widest text-emerald-700">
              Confirm on the latest official notification
            </p>
            <p className="mt-3 text-sm text-gray-700 border-l-2 border-emerald-300 pl-3" data-testid="banking-official-notice-note">
              For banking careers, confirm eligibility, exam details and application dates through the bank or recruiting body’s latest official notification.
            </p>
          </Section>
        )}

        {role.daily_work_preview && (
          <Section id="daily-work" icon={Briefcase} title="Daily Work Preview" accent="teal">
            <p>{role.daily_work_preview}</p>
          </Section>
        )}

        {role.transfers_location_work_pressure && (
          <Section id="transfers" icon={Map} title="Transfers, Location & Work Pressure" accent="amber">
            <p>{role.transfers_location_work_pressure}</p>
          </Section>
        )}

        <AgentWorkforceSection role={role} labels={labels} />
        <AutomationOutlookSection role={role} labels={labels} />

        {learn.length > 0 && (
          <Section id="skills" icon={Wrench} title="Learning Priorities — What to Build Next" accent="teal">
            <div data-testid="bank-preview-learn-next"><Bullets items={learn} tone="teal" /></div>
          </Section>
        )}

        {role.tools_to_learn_or_understand?.length > 0 && (
          <Section id="tools" icon={BookOpen} title="Tools to Learn or Understand" accent="blue">
            <ChipList items={role.tools_to_learn_or_understand} tone="blue" />
          </Section>
        )}

        {(practice || role.useful_student_projects?.length > 0) && (
          <Section id="projects" icon={Lightbulb} title="Practical Exercises" accent="amber">
            <div className="space-y-3">
              {practice && (
                <InfoCard label={`Practice task — ${practice.title || 'try it with fictional data'}`} tone="cream">
                  <div data-testid="bank-preview-practice">
                    <p>{practice.task}</p>
                    {practice.success_check && <p className="mt-2 text-sm"><span className="font-semibold">Success check:</span> {practice.success_check}</p>}
                  </div>
                </InfoCard>
              )}
              {role.useful_student_projects?.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {role.useful_student_projects.map((p, i) => (
                    <div key={i} className="rounded-2xl bg-amber-50 border border-amber-200 p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-amber-700 font-extrabold text-base shrink-0">{String(i + 1).padStart(2, '0')}</span>
                        <p className="text-[15px] text-gray-800 leading-relaxed">{p}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Section>
        )}

        {role.interview_ready_explanation && (
          <Section id="interview" icon={MessageCircle} title="Interview-Ready Explanation" accent="indigo">
            <div className="rounded-2xl bg-violet-50 border border-violet-200 p-4 md:p-5">
              <div className="flex items-start gap-3">
                <Quote size={20} className="text-violet-600 shrink-0 mt-1" />
                <p className="text-[15px] text-gray-800 leading-relaxed italic">{role.interview_ready_explanation}</p>
              </div>
            </div>
          </Section>
        )}

        {role.quick_readiness_test?.length > 0 && (
          <Section id="readiness" icon={Activity} title="Quick Readiness Test" accent="cyan">
            <Bullets items={role.quick_readiness_test} tone="teal" />
          </Section>
        )}

        {/* Knowledge check — quiz_version 2.0.0, learning feedback only */}
        {quiz && (
          <Section id="quiz" icon={Brain} title={H.role_quiz || 'Check Your Understanding'} accent="purple">
            <p className="text-sm text-gray-600 mb-4" data-testid="bank-preview-quiz-policy">
              {shared?.quiz_policy?.purpose || 'Knowledge self-check and reflection; not a validated career-fit test.'}
              {quiz.quiz_version ? ` Quiz version ${quiz.quiz_version}.` : ''}
            </p>
            <QuizYesNoMcq
              quiz={quiz}
              scoringGuide={{ high_fit: parsed.high.text, medium_fit: parsed.medium.text, low_fit: parsed.low.text }}
              resultLabels={{ high: parsed.high.label || 'Strong knowledge check', medium: parsed.medium.label || 'Review the explanations', low: parsed.low.label || 'Start with the basics' }}
              reflectionUnscored
              showExplanations
              onComplete={saveAttempt}
              testidPrefix="bank-preview-quiz"
            />
            {saveState && (
              <p className="mt-3 text-xs text-gray-600" data-testid="bank-preview-quiz-save-state">
                {saveState === 'saving' && 'Saving this attempt…'}
                {saveState === 'saved' && 'Attempt saved (quiz version 2.0.0).'}
                {saveState === 'error' && 'Could not save this attempt — your result is still shown above.'}
              </p>
            )}
            {attempts.length > 0 && (
              <div className="mt-5" data-testid="bank-preview-quiz-history">
                <p className="text-[11px] uppercase font-bold tracking-[0.18em] text-violet-800 mb-2 inline-flex items-center gap-1.5"><History size={13} /> Your previous checks</p>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  {attempts.slice(0, 5).map((a) => (
                    <li key={a.attempt_id} className="flex flex-wrap gap-x-3" data-testid={`bank-quiz-attempt-${a.attempt_id}`}>
                      <span className="font-semibold">{a.score} / {a.total} correct</span>
                      <span>quiz v{a.quiz_version}</span>
                      <span className="text-gray-500">{new Date(a.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>
        )}

        <BankingSharedGuidance shared={shared} />

      </main>

      <Footer />
    </div>
  );
}
